import { ChangeDetectionStrategy, Component, computed, DestroyRef, DOCUMENT, effect, ElementRef, inject, viewChild } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucidePhone, LucideMessageCircle, LucideX } from '@lucide/angular';
import { trapTabKey } from '../../design-system/components/shared/focus-trap';
import { AuthService } from '../../auth/auth.service';
import { EmergencyContactService } from '../services/emergency-contact.service';
import type { ContactDto } from '../../auth/models/auth.models';

@Component({
  selector: 'app-emergency-contact-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    @if (svc.isOpen()) {
      <div class="overlay" (click)="svc.close()">
        <div
          #dialog
          class="box"
          role="dialog"
          aria-modal="true"
          aria-label="Layanan Emergensi 24/7"
          tabindex="-1"
          (keydown)="handleKeydown($event)"
          (click)="$event.stopPropagation()"
        >
          <div class="box__header">
            <span class="box__icon" aria-hidden="true">
              <ng-container [ngComponentOutlet]="phoneIcon" [ngComponentOutletInputs]="{ size: 28, strokeWidth: 2 }" />
            </span>
            <h2 class="box__title">24/7 Layanan Emergensi</h2>
            <button type="button" class="box__close" aria-label="Tutup" (click)="svc.close()">
              <ng-container [ngComponentOutlet]="closeIcon" [ngComponentOutletInputs]="{ size: 20, strokeWidth: 2 }" />
            </button>
          </div>

          <p class="box__desc">Layanan darurat untuk warga binaan dan keluarga</p>

          <div class="box__list">
            @for (c of contacts(); track c.id) {
              <a [href]="contactHref(c)" target="_blank" rel="noopener" class="contact-item">
                <span class="contact-item__icon" aria-hidden="true">
                  @if (c.type === 'whatsapp') {
                    <ng-container [ngComponentOutlet]="whatsappIcon" [ngComponentOutletInputs]="{ size: 20, strokeWidth: 2 }" />
                  } @else {
                    <ng-container [ngComponentOutlet]="phoneIcon" [ngComponentOutletInputs]="{ size: 20, strokeWidth: 2 }" />
                  }
                </span>
                <span class="contact-item__text">
                  <span class="contact-item__label">{{ c.label ?? (c.type === 'whatsapp' ? 'WhatsApp' : 'Telepon') }}</span>
                  <span class="contact-item__value">{{ c.value }}</span>
                </span>
              </a>
            }
          </div>
        </div>
      </div>
    }
  `,
  styles: `
    .overlay {
      position: fixed;
      inset: 0;
      z-index: var(--z-modal);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--space-4);
      background: rgba(32, 37, 31, 0.55);
      animation: overlay-in var(--motion-base);
    }

    .box {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 420px;
      background: var(--color-surface);
      border-radius: var(--radius-xl);
      box-shadow: var(--shadow-raised);
      overflow: hidden;
      animation: box-in var(--motion-base);
    }

    .box__header {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-5) var(--space-5) 0;
    }

    .box__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: var(--color-error-tint);
      color: var(--color-error-strong);
      flex-shrink: 0;
    }

    .box__title {
      flex: 1;
      margin: 0;
      font-size: var(--text-lg);
      font-weight: var(--font-weight-bold);
    }

    .box__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: 0;
      border-radius: var(--radius-full);
      background: var(--color-bg-subtle);
      color: var(--color-text-secondary);
      cursor: pointer;
      flex-shrink: 0;

      &:hover { background: var(--color-border-subtle); color: var(--color-text); }
    }

    .box__desc {
      margin: var(--space-2) var(--space-5) 0;
      color: var(--color-text-secondary);
      font-size: var(--text-sm);
    }

    .box__list {
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
      padding: var(--space-4) var(--space-3) var(--space-5);
    }

    .contact-item {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-3);
      border-radius: var(--radius-lg);
      text-decoration: none;
      transition: background var(--motion-fast);

      &:hover { background: var(--color-bg-subtle); }
    }

    .contact-item__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: var(--radius-full);
      background: var(--color-primary);
      color: #fff;
      flex-shrink: 0;
    }

    .contact-item__text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .contact-item__label {
      font-size: var(--text-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text);
    }

    .contact-item__value {
      font-size: var(--text-md);
      color: var(--color-text-secondary);
    }

    @keyframes overlay-in {
      from { opacity: 0; }
    }

    @keyframes box-in {
      from {
        opacity: 0;
        transform: translateY(16px) scale(0.98);
      }
    }
  `,
})
export class EmergencyContactDialogComponent {
  protected readonly svc = inject(EmergencyContactService);
  private readonly authService = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogEl = viewChild<ElementRef<HTMLElement>>('dialog');

  private previouslyFocused: HTMLElement | null = null;

  protected readonly phoneIcon = LucidePhone;
  protected readonly whatsappIcon = LucideMessageCircle;
  protected readonly closeIcon = LucideX;

  protected readonly contacts = computed<ContactDto[]>(
    () => this.authService.currentUser()?.institution?.contacts ?? [],
  );

  constructor() {
    effect(() => {
      if (this.svc.isOpen()) {
        this.previouslyFocused = this.document.activeElement as HTMLElement | null;
        this.document.body.style.overflow = 'hidden';
        setTimeout(() => this.dialogEl()?.nativeElement.focus());
      } else {
        this.document.body.style.overflow = '';
        this.previouslyFocused?.focus();
        this.previouslyFocused = null;
      }
    });

    this.destroyRef.onDestroy(() => {
      this.document.body.style.overflow = '';
    });
  }

  protected handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.svc.close();
      return;
    }
    const surface = this.dialogEl()?.nativeElement;
    if (surface) trapTabKey(event, surface);
  }

  protected contactHref(c: ContactDto): string {
    if (c.type === 'whatsapp') {
      let digits = c.value.replace(/\D/g, '');
      if (digits.startsWith('0')) digits = '62' + digits.slice(1);
      return `https://wa.me/${digits}`;
    }
    return `tel:${c.value}`;
  }
}
